import { useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { ConfirmDialog, FormDialog } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useLookup } from '@/hooks/useLookup'
import { formProblem } from '@/lib/api/fieldErrors'
import { useGrantMutations, useGrants, useRolePicker } from '@/features/admin'
import type { GrantView } from '@/features/admin'

/**
 * One person's roles, from the users list.
 *
 * <p>Roles were only ever given at invitation or on a separate grants screen nobody found, so
 * "give Aisha payroll access" had no button anywhere near Aisha. This is the same grant and the
 * same revoke the grants screen uses — the server still refuses a combination that breaks
 * segregation of duties, and says why under the form, in its own words.
 *
 * <p>A grant is per legal entity, or for every entity when none is chosen. Revoking keeps the
 * row for the audit trail; only live grants are listed.
 */
export function UserRolesDialog({
  user, open, onOpenChange,
}: {
  user: { id: string; email: string; fullName: string } | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const grants = useGrants(user?.id)
  const roles = useRolePicker(open)
  const entities = useLookup<{ id: string; name: string }>('legalEntities', open)
  const { create, revoke } = useGrantMutations()

  const [roleId, setRoleId] = useState('')
  const [legalEntityId, setLegalEntityId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [roleError, setRoleError] = useState<string | null>(null)
  const [revoking, setRevoking] = useState<GrantView | null>(null)
  const [changed, setChanged] = useState(false)

  const live = (grants.data ?? []).filter((grant) => grant.live && grant.userId === user?.id)

  async function add() {
    if (!user) return
    setError(null)
    setRoleError(null)
    if (!roleId) {
      setRoleError('Choose a role.')
      return
    }
    try {
      await create.mutateAsync({
        userId: user.id,
        roleId,
        legalEntityId: legalEntityId || null,
        reason: 'Granted from the users list',
      })
      setRoleId('')
      setLegalEntityId('')
      setChanged(true)
    } catch (caught) {
      const problem = formProblem(caught, 'That role could not be granted.')
      setRoleError(problem.fields.roleId ?? null)
      setError(problem.fields.roleId ? null : problem.summary)
    }
  }

  return (
    <>
      <FormDialog
        open={open && !revoking}
        onOpenChange={(next) => {
          if (!next) {
            setError(null); setRoleError(null); setRoleId(''); setLegalEntityId(''); setChanged(false)
          }
          onOpenChange(next)
        }}
        title={user ? `Roles for ${user.fullName}` : 'Roles'}
        description="What this person can do, and in which legal entity."
        size="lg"
        submitLabel="Done"
        error={error}
        onSubmit={() => onOpenChange(false)}
      >
        <div className="space-y-4">
          {grants.isPending && <Skeleton className="h-16" />}
          {grants.isSuccess && live.length === 0 && (
            <p className="text-sm text-[var(--color-muted)]">
              No roles yet — this person can sign in but do nothing beyond their own records.
            </p>
          )}
          {live.length > 0 && (
            <ul className="divide-y divide-[var(--color-border)] rounded-md border border-[var(--color-border)]">
              {live.map((grant) => (
                <li key={grant.id} className="flex items-center justify-between gap-2 p-2 text-sm">
                  <span>
                    <span className="font-medium text-[var(--color-heading)]">{grant.roleName}</span>
                    <span className="ms-2 text-xs text-[var(--color-muted)]">
                      {grant.legalEntityName ?? 'All entities'}
                    </span>
                  </span>
                  <Button
                    type="button" variant="ghost" size="sm"
                    aria-label={`Revoke ${grant.roleName}`}
                    onClick={() => setRevoking(grant)}
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {changed && (
            // Permissions travel in the sign-in token, which is renewed every few minutes and
            // at every sign-in; until then the person still holds what they signed in with.
            <p role="status" className="text-sm text-[var(--color-success)]">
              Saved. They will see the change the next time they sign in, or within a few minutes
              if they are signed in now.
            </p>
          )}
          <div className="space-y-3 rounded-md border border-[var(--color-border)] p-3">
            <p className="text-sm font-medium text-[var(--color-heading)]">Add a role</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="user-role">Role</Label>
                <Select
                  id="user-role" className="w-full" value={roleId}
                  aria-invalid={Boolean(roleError)}
                  onChange={(event) => setRoleId(event.target.value)}
                >
                  <option value="">Choose a role</option>
                  {roles.data?.map((role) => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </Select>
                {roleError && (
                  <p role="alert" className="mt-1 text-xs text-[var(--color-danger)]">{roleError}</p>
                )}
              </div>
              <div>
                <Label htmlFor="user-role-entity">Legal entity</Label>
                <Select
                  id="user-role-entity" className="w-full" value={legalEntityId}
                  onChange={(event) => setLegalEntityId(event.target.value)}
                >
                  <option value="">All entities</option>
                  {(entities.data ?? []).map((entity) => (
                    <option key={entity.id} value={entity.id}>{entity.name}</option>
                  ))}
                </Select>
              </div>
            </div>
            <Button type="button" size="sm" disabled={create.isPending} onClick={() => { void add() }}>
              <Plus className="size-4" aria-hidden="true" />
              Add role
            </Button>
          </div>
        </div>
      </FormDialog>

      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(next) => { if (!next) setRevoking(null) }}
        title={`Revoke ${revoking?.roleName ?? 'this role'}?`}
        description="It stops working at once. The grant stays in the audit trail."
        confirmLabel="Revoke"
        loading={revoke.isPending}
        onConfirm={() => {
          if (!revoking) return
          revoke.mutate(revoking.id, {
            onSuccess: () => setChanged(true),
            onSettled: () => setRevoking(null),
            onError: (caught) => setError(formProblem(caught, 'That role could not be revoked.').summary),
          })
        }}
      />
    </>
  )
}
